import React, { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { redirectToAuthFrom, showLoginModal, selectCurrentUserId } from 'features/auth/authSlice';
import { createGameAction, resetCreateGameStateAction, selectCreatedGameId, selectCreating } from '../../createGame/createGameSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import logging from 'utils/logging';
import { clearAuthReturnPath, saveAuthReturnPath } from 'features/auth/authReturnPath';
import Analytics from 'analytics';
import { EVENTS } from 'analytics/events';
import { Button, Card, Text } from 'components';
import CreateHeader from '../CreateHeader';
import TitleField from '../fields/TitleField';
import LengthField from '../fields/LengthField';
import { MANUAL_LENGTH } from '../fields/options';
import { Pencil } from '../icons';
import '../create.css';

const log = logging.getLogger("ManualCreate");

const MANUAL_CREATE_DRAFT_KEY = 'groupardy.manualCreateDraft';
const MANUAL_CREATE_PENDING_KEY = 'groupardy.manualCreatePending';

const loadSavedDraft = () => {
  try {
    return JSON.parse(localStorage.getItem(MANUAL_CREATE_DRAFT_KEY)) || {};
  } catch (error) {
    log.warn("Failed to load saved manual create draft", error);
    return {};
  }
};

const saveDraft = (draft) => {
  localStorage.setItem(MANUAL_CREATE_DRAFT_KEY, JSON.stringify(draft));
};

const clearPendingDraft = () => {
  localStorage.removeItem(MANUAL_CREATE_DRAFT_KEY);
  localStorage.removeItem(MANUAL_CREATE_PENDING_KEY);
};

const ManualCreate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const savedDraft = loadSavedDraft();
  const [gameName, setGameName] = useState(savedDraft.gameName || '');
  const [gameLength, setGameLength] = useState(savedDraft.gameLength || 'standard');

  const creating = useSelector(selectCreating);
  const userId = useSelector(selectCurrentUserId);
  const createdGameId = useSelector(selectCreatedGameId);

  useEffect(() => {
    Analytics.track(EVENTS.GAME_CREATE_STARTED, { creation_mode: "manual" });
  }, []);

  useEffect(() => {
    if (createdGameId) {
      dispatch(resetCreateGameStateAction());
      clearPendingDraft();
      clearAuthReturnPath();
      log.debug("Pushing to edit new game: ", createdGameId);
      navigate(`/manage/game/${createdGameId}/details`);
    }
  }, [createdGameId, dispatch, navigate]);

  const buildGameOptions = useCallback((managerId) => {
    const numRounds = gameLength === 'short' ? 1 : gameLength === 'standard' ? 2 : 3;
    const numCategories = gameLength === 'short' ? 5 : gameLength === 'standard' ? 6 : 7;
    const numQuestions = 5;

    log.debug("Creating game with values: ", { name: gameName, length: gameLength });

    return {
      gameName,
      categoryCount: numCategories,
      roundCount: numRounds,
      questionCount: numQuestions,
      description: '',
      managerId,
      useLorem: false,
    };
  }, [gameLength, gameName]);

  useEffect(() => {
    if (!userId || creating || !localStorage.getItem(MANUAL_CREATE_PENDING_KEY)) {
      return;
    }

    localStorage.removeItem(MANUAL_CREATE_PENDING_KEY);
    dispatch(createGameAction(buildGameOptions(userId)));
  }, [buildGameOptions, creating, dispatch, userId]);

  const handleCreateGame = () => {
    if (!userId) {
      saveDraft({ gameName, gameLength });
      localStorage.setItem(MANUAL_CREATE_PENDING_KEY, 'true');
      saveAuthReturnPath(`${location.pathname}${location.search}`);
      dispatch(redirectToAuthFrom({ destination: `${location.pathname}${location.search}` }));
      dispatch(showLoginModal());
      return;
    }

    dispatch(createGameAction(buildGameOptions(userId)));
  };

  return (
    <div className="cg-page surface-manager">
      <CreateHeader />
      <main className="cg-main">
        <div className="cg-stage narrow">
          <Card padding="lg">
            <Text variant="eyebrow" tone="accent" className="cg-step-eyebrow">New game</Text>
            <Text variant="h2" className="cg-step-title">Build your own game</Text>
            <Text variant="body" tone="muted" className="cg-step-sub">Name it and pick a length — then build your board clue by clue.</Text>

            <div className="cg-step-body">
              <TitleField
                value={gameName}
                onChange={setGameName}
                label="Game name"
                placeholder="e.g., Movie Night Trivia, Science Challenge"
              />
              <div className="cg-field">
                <span className="cg-field-label">Game length</span>
                <LengthField options={MANUAL_LENGTH} value={gameLength} onChange={setGameLength} />
              </div>
            </div>

            <div className="cg-wiz-foot">
              <button type="button" className="cg-link-btn" onClick={() => navigate('/create')}>
                ← Back to options
              </button>
              <Button
                variant="cta"
                loading={creating}
                startIcon={<Pencil size={16} />}
                disabled={!gameName.trim() || creating}
                onClick={handleCreateGame}
              >
                {creating ? 'Creating…' : 'Create Game'}
              </Button>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default ManualCreate;
